import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

interface Streak {
  current_streak: number;
  longest_streak: number;
  last_active_date: string | null;
  streak_protected_until: string | null;
  freezes_remaining: number;
}

interface StreakMilestone {
  days: number;
  multiplier: number;
  name: string;
}

const STREAK_MILESTONES: StreakMilestone[] = [
  { days: 3, multiplier: 1.25, name: 'Getting Started' },
  { days: 7, multiplier: 1.5, name: 'Week Warrior' },
  { days: 14, multiplier: 1.75, name: 'Fortnight Fashion' },
  { days: 30, multiplier: 2, name: 'Monthly Maven' },
  { days: 100, multiplier: 2.5, name: 'Century Club' },
  { days: 365, multiplier: 3, name: 'Year of Style' },
];

export const useStreaks = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [streak, setStreak] = useState<Streak | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStreak = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('user_streaks')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching streak:', error);
    }

    if (data) {
      setStreak(data as Streak);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchStreak();
  }, [fetchStreak]);

  const getCurrentMultiplier = useCallback(() => {
    if (!streak) return 1;
    
    const milestone = [...STREAK_MILESTONES]
      .reverse()
      .find(m => streak.current_streak >= m.days);
    
    return milestone?.multiplier || 1;
  }, [streak]);

  const getNextMilestone = useCallback(() => {
    if (!streak) return STREAK_MILESTONES[0];
    
    return STREAK_MILESTONES.find(m => m.days > streak.current_streak) || null;
  }, [streak]);

  const checkAndUpdateStreak = useCallback(async () => {
    if (!user) return null;

    const today = new Date().toISOString().split('T')[0];
    
    // Fetch current streak
    const { data: currentStreak, error: fetchError } = await supabase
      .from('user_streaks')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error fetching streak:', fetchError);
      return null;
    }

    // First time user - create streak
    if (!currentStreak) {
      const { data: newStreak, error: insertError } = await supabase
        .from('user_streaks')
        .insert({
          user_id: user.id,
          current_streak: 1,
          longest_streak: 1,
          last_active_date: today,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating streak:', insertError);
        return null;
      }

      toast({
        title: '🔥 Streak Started!',
        description: 'Your style journey begins! Come back tomorrow to keep it going.',
      });

      setStreak(newStreak as Streak);
      return { isNew: true, streak: 1, milestone: null };
    }

    const lastActive = currentStreak.last_active_date;
    
    // Already logged in today
    if (lastActive === today) {
      return { isNew: false, streak: currentStreak.current_streak, milestone: null };
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    let newCurrentStreak = 1;
    let streakBroken = false;

    // Check if streak continues or is broken
    if (lastActive === yesterdayStr) {
      // Streak continues!
      newCurrentStreak = currentStreak.current_streak + 1;
    } else {
      // Check for streak protection (premium feature)
      const protectedUntil = currentStreak.streak_protected_until;
      if (protectedUntil && new Date(protectedUntil) >= new Date(today)) {
        newCurrentStreak = currentStreak.current_streak + 1;
      } else if (currentStreak.freezes_remaining > 0) {
        // Use a freeze
        newCurrentStreak = currentStreak.current_streak + 1;
        toast({
          title: '❄️ Streak Freeze Used!',
          description: `Your streak was saved! ${currentStreak.freezes_remaining - 1} freezes remaining.`,
        });
      } else {
        // Streak broken
        streakBroken = true;
        if (currentStreak.current_streak >= 3) {
          toast({
            title: '💔 Streak Lost',
            description: `Your ${currentStreak.current_streak}-day streak ended. Start fresh today!`,
            variant: 'destructive',
          });
        }
      }
    }

    const newLongestStreak = Math.max(newCurrentStreak, currentStreak.longest_streak);
    const freezesUsed = !streakBroken && lastActive !== yesterdayStr && currentStreak.freezes_remaining > 0;

    const { data: updatedStreak, error: updateError } = await supabase
      .from('user_streaks')
      .update({
        current_streak: newCurrentStreak,
        longest_streak: newLongestStreak,
        last_active_date: today,
        freezes_remaining: freezesUsed 
          ? currentStreak.freezes_remaining - 1 
          : currentStreak.freezes_remaining,
      })
      .eq('user_id', user.id)
      .select()
      .single();

    if (updateError) {
      console.error('Error updating streak:', updateError);
      return null;
    }

    setStreak(updatedStreak as Streak);

    // Check for milestone
    const milestone = STREAK_MILESTONES.find(m => m.days === newCurrentStreak);
    if (milestone) {
      toast({
        title: `🎉 ${milestone.name}!`,
        description: `${newCurrentStreak}-day streak! You now earn ${milestone.multiplier}x bonus points!`,
      });
      return { isNew: true, streak: newCurrentStreak, milestone };
    }

    if (!streakBroken && newCurrentStreak > 1) {
      toast({
        title: `🔥 ${newCurrentStreak} Day Streak!`,
        description: `Keep it up! ${getCurrentMultiplier()}x point bonus active.`,
      });
    }

    return { isNew: true, streak: newCurrentStreak, milestone: null };
  }, [user, toast, getCurrentMultiplier]);

  const addStreakFreeze = useCallback(async (count: number = 1) => {
    if (!user || !streak) return false;

    const { error } = await supabase
      .from('user_streaks')
      .update({
        freezes_remaining: streak.freezes_remaining + count,
      })
      .eq('user_id', user.id);

    if (error) {
      console.error('Error adding streak freeze:', error);
      return false;
    }

    await fetchStreak();
    return true;
  }, [user, streak, fetchStreak]);

  const protectStreak = useCallback(async (days: number = 1) => {
    if (!user || !profile?.is_premium) return false;

    const protectedUntil = new Date();
    protectedUntil.setDate(protectedUntil.getDate() + days);

    const { error } = await supabase
      .from('user_streaks')
      .update({
        streak_protected_until: protectedUntil.toISOString().split('T')[0],
      })
      .eq('user_id', user.id);

    if (error) {
      console.error('Error protecting streak:', error);
      return false;
    }

    await fetchStreak();
    toast({
      title: '🛡️ Streak Protected!',
      description: `Your streak is safe for the next ${days} day(s).`,
    });
    return true;
  }, [user, profile, fetchStreak, toast]);

  return {
    streak,
    loading,
    getCurrentMultiplier,
    getNextMilestone,
    checkAndUpdateStreak,
    addStreakFreeze,
    protectStreak,
    milestones: STREAK_MILESTONES,
  };
};
